/**
 * A tiny async key/value store.
 *
 * Wirespan runs both as a hosted artifact — where `window.storage` is
 * provided — and as a self-hosted PWA, where it doesn't exist. Try the host's
 * store first, fall back to `localStorage`, and degrade to in-memory if both
 * are unavailable (private browsing, storage disabled).
 */
export interface KeyValueStore {
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
}

interface HostStorage {
  get(key: string): Promise<{ value?: string | null } | null>;
  set(key: string, value: string): Promise<unknown>;
}

function hostStorage(): HostStorage | null {
  const s = (globalThis as { storage?: HostStorage }).storage;
  return s && typeof s.get === 'function' && typeof s.set === 'function' ? s : null;
}

function localStore(): Storage | null {
  try {
    // Prefer the document's own store; they are the same object in a browser.
    // Safari throws on access in some privacy modes rather than returning null.
    const win = (globalThis as { window?: { localStorage?: Storage } }).window;
    const ls = win?.localStorage ?? globalThis.localStorage;
    // Locked-down browsers — and Node's experimental global — can hand back
    // something that isn't a working Storage.
    return ls && typeof ls.getItem === 'function' && typeof ls.setItem === 'function' ? ls : null;
  } catch {
    return null;
  }
}

const memory = new Map<string, string>();

export const storage: KeyValueStore = {
  async get(key) {
    const host = hostStorage();
    if (host) {
      try {
        const r = await host.get(key);
        if (r && r.value != null) return r.value;
      } catch {
        /* fall through to localStorage */
      }
    }
    try {
      const ls = localStore();
      if (ls) return ls.getItem(key);
    } catch {
      /* fall through to memory */
    }
    return memory.get(key) ?? null;
  },

  async set(key, value) {
    memory.set(key, value);
    const host = hostStorage();
    if (host) {
      try {
        await host.set(key, value);
        return;
      } catch {
        /* fall through to localStorage */
      }
    }
    try {
      localStore()?.setItem(key, value);
    } catch {
      /* memory copy is all we get */
    }
  },
};
